import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["isReplacement"];
import has from 'hs-lodash/has';
import { registerMutation, registerQuery, useDataFetchingClient, useMutation, useQuery } from 'data-fetching-client';
import get from 'hs-lodash/get';
import { batchActionOnAllSidebarNotifications, batchActionOnSomeSidebarNotifications, getV3NotificationsList, singleActionOnNotification } from '../../../../api/notificationsApi';
import { SIDEBAR_BATCH_ACTIONS } from '../../../../constants';
import { useCallback, useMemo } from 'react';
import Raven from 'raven-js';
import { excludeAtIndex, replaceAtIndex } from '../../../../utils/ArrayUtils';
import { GET_SIDEBAR_UNREAD_COUNT } from './SidebarUnreadCountQuery';
import { ALL, TRASH, UNREAD } from '../constants';
export const SIDEBAR_NOTIFICATIONS_QUERY_NAME = 'sidebar';
const GET_SIDEBAR_NOTIFICATIONS = registerQuery({
  fieldName: SIDEBAR_NOTIFICATIONS_QUERY_NAME,
  // fieldName MUST match an entry in the DataFetchingClient's queryFieldPolicies config
  args: ['beforeId', 'filterOption', 'sidebarTabState'],
  fetcher: getV3NotificationsList
});
export const useSidebarNotificationsQuery = variables => {
  return useQuery(GET_SIDEBAR_NOTIFICATIONS, {
    fetchPolicy: 'cache-and-network',
    variables,
    notifyOnNetworkStatusChange: true
  });
};

/**
 * Evicts all caches sidebar notifications lists we have used so far with the exception of
 * the current query, identified by `currentVariables`. The effect of which means that the
 * lists will be loaded fresh via the network when next requested.
 *
 * The intention of this function is to be called when the current list has mutated data
 * that may have invalidated data cached by another tab/filter combination so a reload
 * is required to ensure we are up to data if the user returns to that tab/filter
 *
 * @param cache the applications data fetching client cache
 * @param currentVariables the arguments for the present query whose data will be preserved
 */
const evictAllSidebarNotificationListCachesExcept = (cache, currentVariables) => {
  // The DataFetchingClient's cache is actually an Apollo MemoryCache, the patterns for
  // updating it and locating data/keys are the same
  const currentCacheKey = `${SIDEBAR_NOTIFICATIONS_QUERY_NAME}:${JSON.stringify(currentVariables)}`;
  const allQueryFields = get(cache.extract(), 'ROOT_QUERY', {});
  Object.keys(allQueryFields).forEach(key => {
    // Evict any data that is sidebar notifications and not the variables currently in use
    if (key.startsWith(`${SIDEBAR_NOTIFICATIONS_QUERY_NAME}`) && key !== currentCacheKey) {
      var _key$split$;
      const args = JSON.parse((_key$split$ = key.split(`${SIDEBAR_NOTIFICATIONS_QUERY_NAME}:`)[1]) !== null && _key$split$ !== void 0 ? _key$split$ : '{}');
      cache.evict({
        args,
        fieldName: SIDEBAR_NOTIFICATIONS_QUERY_NAME,
        id: 'ROOT_QUERY'
      });
    }
  });
};
export const mergeSidebarNotifications = (existing, _ref, {
  variables
}) => {
  let {
      isReplacement
    } = _ref,
    incoming = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (!existing || isReplacement) {
    return incoming;
  }

  // Could be a refetch
  if (existing && !has(variables, 'beforeId')) {
    // Arguments did not include a beforeId, therefore must be a refetch of the first page
    // consider this a replacement of the existing cache
    return incoming;
  }

  // Must be a new page, append to data instead of replacing it
  return {
    hasMore: incoming.hasMore,
    // Incoming is source of truth for any more notifications,
    notifications: [...existing.notifications, ...incoming.notifications]
  };
};
const bulkUpdateNotifications = async ({
  action,
  notificationIds
}) => {
  try {
    await batchActionOnSomeSidebarNotifications({
      action,
      notificationIds
    });
  } catch (e) {
    throw new Error(`Failed to bulk update notifications for bulk update: ${e instanceof Error ? e.message : e}`, {
      cause: e
    });
  }
  return true;
};
const updateAllNotifications = async ({
  action,
  notificationIds,
  sidebarState,
  types
}) => {
  try {
    await batchActionOnAllSidebarNotifications({
      action,
      notificationIds,
      sidebarState,
      filterType: types
    });
  } catch (e) {
    throw new Error(`Failed to bulk update notifications for update All: ${e instanceof Error ? e.message : e}`, {
      cause: e
    });
  }
  return true;
};
const bulkUpdateExistingNotifications = (existingNotifications, {
  action,
  selectAllIsApplied,
  sidebarState,
  notificationIds
}) => {
  if (sidebarState !== 'UNREAD' && (action === SIDEBAR_BATCH_ACTIONS.MARK_AS_READ || action === SIDEBAR_BATCH_ACTIONS.MARK_AS_UNREAD)) {
    const isCandidateForUpdate = existingNotification => {
      const hasExclusions = selectAllIsApplied && notificationIds.length > 0;
      if (selectAllIsApplied && !hasExclusions) {
        return true;
      }
      const isChecked = notificationIds.includes(existingNotification.id);
      return hasExclusions ? !isChecked : isChecked;
    };
    const desiredConvertToReadState = action === SIDEBAR_BATCH_ACTIONS.MARK_AS_READ;
    return existingNotifications.map(existingNotification => {
      if (existingNotification.read !== desiredConvertToReadState && isCandidateForUpdate(existingNotification)) {
        return Object.assign({}, existingNotification, {
          read: desiredConvertToReadState
        });
      }
      return existingNotification;
    });
  }

  // Remarkably with the supported options in the UI all remaining bulk actions will result in us filtering the
  // selected notifications from the list without needing to change values in any of them
  if (selectAllIsApplied) {
    return notificationIds.length === 0 ? [] : existingNotifications.filter(existingNotification => notificationIds.includes(existingNotification.id));
  }
  return existingNotifications.filter(existingNotification => !notificationIds.includes(existingNotification.id));
};
const BULK_UPDATE_MUTATION = registerMutation({
  fieldName: 'updatedStatus',
  fetcher: bulkUpdateNotifications
});
const UPDATE_ALL_MUTATION = registerMutation({
  fieldName: 'updatedStatus',
  fetcher: updateAllNotifications
});
const updateSingleNotification = async ({
  action,
  notificationId
}) => {
  try {
    await singleActionOnNotification({
      action,
      notificationId
    });
  } catch (e) {
    throw new Error(`Failed to update a notification: ${e instanceof Error ? e.message : e}`, {
      cause: e
    });
  }
  return true;
};
const UPDATE_SINGLE_MUTATION = registerMutation({
  fieldName: 'updatedStatus',
  fetcher: updateSingleNotification
});
export const useMutateSidebar = variables => {
  const {
    cache: appCache
  } = useDataFetchingClient();

  // Queries to refetch after a destructive action
  const refetchQueries = useMemo(() => {
    const unreadCount = {
      query: GET_SIDEBAR_UNREAD_COUNT,
      variables: {
        sidebarState: UNREAD
      }
    };
    if (variables.sidebarTabState === 'ALL') {
      const allCount = {
        query: GET_SIDEBAR_UNREAD_COUNT,
        variables: {
          sidebarState: ALL
        }
      };
      return [unreadCount, allCount];
    } else if (variables.sidebarTabState === 'TRASH') {
      const trashCount = {
        query: GET_SIDEBAR_UNREAD_COUNT,
        variables: {
          sidebarState: TRASH
        }
      };
      return [unreadCount, trashCount];
    }
    return [unreadCount];
  }, [variables.sidebarTabState]);
  const [bulkUpdate, {
    loading: isMutatingBulkUpdate
  }] = useMutation(BULK_UPDATE_MUTATION, {
    onError: error => {
      Raven.captureException(error, {
        extra: {
          message: 'Failed to bulk update notifications'
        }
      });
    },
    optimisticResponse: () => ({
      updatedStatus: true
    }),
    update: (cache, {
      data
    }, {
      variables: params
    }) => {
      const existing = cache.readQuery({
        query: GET_SIDEBAR_NOTIFICATIONS,
        variables
      });
      if (!data || !existing || !params) {
        // This shouldn't be a reachable branch for this mutation but should we reach here
        // the best we can do is return without changing anything
        return;
      }
      const updatedNotifications = bulkUpdateExistingNotifications(existing.sidebar.notifications, Object.assign({}, params, {
        selectAllIsApplied: false
      }));
      const updatedData = {
        sidebar: Object.assign({}, existing.sidebar, {
          isReplacement: true,
          notifications: updatedNotifications
        })
      };
      cache.writeQuery({
        data: updatedData,
        query: GET_SIDEBAR_NOTIFICATIONS,
        variables
      });
    }
  });

  /**
   * Update the read/deleted status for bulk notifications with up to 100.
   *
   * The query cache data will optimistically be updated and should the request
   * fail the original data will be restored.
   */
  const bulkUpdateSidebarNotifications = useCallback(async ({
    action,
    notificationIds,
    sidebarState
  }) => {
    evictAllSidebarNotificationListCachesExcept(appCache, variables);
    await bulkUpdate({
      awaitRefetchQueries: true,
      refetchQueries,
      variables: {
        action,
        notificationIds,
        sidebarState
      }
    });
  }, [appCache, variables, bulkUpdate, refetchQueries]);
  const [updateAll, {
    loading: isMutatingBulkUpdateAll
  }] = useMutation(UPDATE_ALL_MUTATION, {
    onError: error => {
      Raven.captureException(error, {
        extra: {
          message: 'Failed to update all notifications'
        }
      });
    },
    optimisticResponse: () => ({
      updatedStatus: true
    }),
    update: (cache, {
      data
    }, {
      variables: params
    }) => {
      const existing = cache.readQuery({
        query: GET_SIDEBAR_NOTIFICATIONS,
        variables
      });
      if (!data || !existing || !params) {
        // This shouldn't be a reachable branch for this mutation but should we reach here
        // the best we can do is return without changing anything
        return;
      }
      const updatedNotifications = bulkUpdateExistingNotifications(existing.sidebar.notifications, Object.assign({}, params, {
        selectAllIsApplied: true
      }));
      const updatedData = {
        sidebar: Object.assign({}, existing.sidebar, {
          isReplacement: true,
          notifications: updatedNotifications
        })
      };
      cache.writeQuery({
        data: updatedData,
        query: GET_SIDEBAR_NOTIFICATIONS,
        variables
      });
    }
  });

  /**
   * Update the read/deleted status for all notifications.
   *
   * The query cache data will optimistically be updated and should the request
   * fail the original data will be restored.
   */
  const updateAllSidebarNotifications = useCallback(async ({
    action,
    notificationIds,
    types,
    sidebarState
  }) => {
    evictAllSidebarNotificationListCachesExcept(appCache, variables);
    await updateAll({
      awaitRefetchQueries: true,
      refetchQueries,
      variables: {
        action,
        notificationIds,
        sidebarState,
        types
      }
    });
  }, [appCache, variables, updateAll, refetchQueries]);
  const [updateOne, {
    loading: isMutatingSingleNotification
  }] = useMutation(UPDATE_SINGLE_MUTATION, {
    onError: error => {
      Raven.captureException(error, {
        extra: {
          message: 'Failed to update a notification'
        }
      });
    },
    optimisticResponse: () => ({
      updatedStatus: true
    }),
    update: (cache, {
      data
    }, {
      variables: params
    }) => {
      const existing = cache.readQuery({
        query: GET_SIDEBAR_NOTIFICATIONS,
        variables
      });
      if (!data || !existing || !params) {
        // This shouldn't be a reachable branch for this mutation but should we reach here
        // the best we can do is return without changing anything
        return;
      }
      const existingPosition = existing.sidebar.notifications.findIndex(existingNotification => existingNotification.id === params.notificationId);
      if (existingPosition === -1) {
        // Can't update something that isn't there
        return;
      }
      const notificationToUpdate = existing.sidebar.notifications[existingPosition];
      if (params.action === SIDEBAR_BATCH_ACTIONS.MARK_AS_READ && notificationToUpdate.read) {
        // Nothing needs to change
        return;
      }
      const notifications = params.action === SIDEBAR_BATCH_ACTIONS.MARK_AS_READ || params.action === SIDEBAR_BATCH_ACTIONS.MARK_AS_UNREAD ? replaceAtIndex(existing.sidebar.notifications, existingPosition, Object.assign({}, notificationToUpdate, {
        read: params.action === SIDEBAR_BATCH_ACTIONS.MARK_AS_READ
      })) : excludeAtIndex(existing.sidebar.notifications, existingPosition);
      const updatedData = {
        sidebar: Object.assign({}, existing.sidebar, {
          isReplacement: true,
          notifications
        })
      };
      cache.writeQuery({
        data: updatedData,
        query: GET_SIDEBAR_NOTIFICATIONS,
        variables
      });
    }
  });

  /**
   * Update the read/deleted status for a single notification.
   *
   * The query cache data will optimistically be updated and should the request
   * fail the original data will be restored.
   */
  const updateOneSidebarNotification = useCallback(async ({
    action,
    notificationId
  }) => {
    evictAllSidebarNotificationListCachesExcept(appCache, variables);
    await updateOne({
      variables: {
        action,
        notificationId
      }
    });
  }, [appCache, variables, updateOne]);
  return {
    bulkUpdateSidebarNotifications,
    mutating: isMutatingBulkUpdate || isMutatingBulkUpdateAll || isMutatingSingleNotification,
    updateOneSidebarNotification,
    updateAllSidebarNotifications
  };
};